import React, { useMemo, useState } from 'react';

import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams, useNavigate } from 'react-router-dom';

import defaultClient from '@src/api/clients/default';
import { FormBase, Submit, TextField } from '@src/components/form';
import { useMessage } from '@src/context';
import useForm from '@src/hooks/useForm';

const requestSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

const confirmSchema = yup.object().shape({
  new_password1: yup.string().min(8, 'Use at least 8 characters').required('New password is required'),
  new_password2: yup
    .string()
    .oneOf([yup.ref('new_password1'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

export default function PasswordReset() {
  const [params] = useSearchParams();
  const message = useMessage();
  const navigate = useNavigate();

  const uid = params.get('uid');
  const token = params.get('token');
  const isConfirm = Boolean(uid && token);

  const requestForm = useForm({ schema: requestSchema, defaultValues: { email: '' }, resetAfterSuccessSubmit: true });
  const confirmForm = useForm({
    schema: confirmSchema,
    defaultValues: { new_password1: '', new_password2: '' },
    resetAfterSuccessSubmit: true,
  });

  const title = useMemo(() => (isConfirm ? 'Set a new password' : 'Reset your password'), [isConfirm]);

  const [notFoundEmail, setNotFoundEmail] = useState(null);

  const handleRequest = ({ email }) => {
    setNotFoundEmail(null);
    return defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/password/reset/',
        method: 'POST',
        payload: { email },
      })
      .then(() => {
        message.success('Check your email for a reset link.');
      })
      .catch((error) => {
        const emailErrors = error?.response?.data?.email;
        const isNotFound =
          Array.isArray(emailErrors) && emailErrors.some((msg) => String(msg).includes('No account found'));
        if (isNotFound) {
          setNotFoundEmail(email);
        } else {
          message.error('Unable to send reset link.');
        }
      });
  };

  const handleConfirm = (payload) =>
    defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/password/reset/confirm/',
        method: 'POST',
        payload: { ...payload, uid, token },
      })
      .then(() => {
        message.success('Password updated. You can now sign in.');
        navigate('/auth/login');
      })
      .catch(() => {
        message.error('Unable to update password. The link may be invalid or expired.');
      });

  return (
    <Box sx={{ maxWidth: 480, width: '100%' }}>
      <Card>
        <CardHeader title={title} />
        <CardContent>
          {isConfirm ? (
            <FormBase onSubmit={confirmForm.handleSubmit({ onSubmit: handleConfirm })}>
              <Stack spacing={2}>
                <TextField
                  control={confirmForm.control}
                  fullWidth
                  label="New password"
                  name="new_password1"
                  type="password"
                  autoComplete="new-password"
                />
                <TextField
                  control={confirmForm.control}
                  fullWidth
                  label="Confirm new password"
                  name="new_password2"
                  type="password"
                  autoComplete="new-password"
                />
                <Submit variant="contained">Update password</Submit>
                <Button
                  onClick={() => {
                    navigate('/auth/login');
                  }}
                  type="button"
                >
                  Back to sign in
                </Button>
              </Stack>
            </FormBase>
          ) : (
            <FormBase onSubmit={requestForm.handleSubmit({ onSubmit: handleRequest })}>
              <Stack spacing={2}>
                {notFoundEmail ? (
                  <Alert severity="warning">
                    <AlertTitle>No account found for {notFoundEmail}</AlertTitle>
                    <Typography variant="body2">
                      This email isn&apos;t registered in our system. To get access, reach out to your Lead Gurus
                      contact to have an account invite sent to you, or ask a team member at your company who already
                      has portal access to invite you.
                    </Typography>
                  </Alert>
                ) : null}
                <TextField control={requestForm.control} fullWidth label="Email" name="email" autoComplete="email" />
                <Submit variant="contained">Send reset link</Submit>
                <Button
                  onClick={() => {
                    navigate('/auth/login');
                  }}
                  type="button"
                >
                  Back to sign in
                </Button>
              </Stack>
            </FormBase>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

PasswordReset.displayName = 'PasswordReset';
